import { computed, ref } from 'vue';
import callClientPortal from '../../lib/callClientPortal';
import { EDITAGENT_API_BASE } from './editAgentApi';

export type EditAgentCreditsStatus = {
  unlimited: boolean;
  remainingCredits: number | null;
  canSend: boolean;
  consumed?: boolean;
};

declare global {
  interface Window {
    fillEditAgentCredits?: (
      remainingCredits: number
    ) => Promise<EditAgentCreditsStatus | null>;
    consumeEditAgentCredits?: () => Promise<EditAgentCreditsStatus | null>;
  }
}

export function useEditAgentCredits(enabled = true) {
  const status = ref<EditAgentCreditsStatus | null>(null);
  const loading = ref(false);
  const initialized = ref(false);
  const error = ref('');

  const isUnlimited = computed(() => status.value?.unlimited === true);
  const canSend = computed(() => {
    if (!enabled) return true;
    if (!initialized.value) return true;
    return status.value?.canSend !== false;
  });
  const remainingCredits = computed(
    () => status.value?.remainingCredits ?? null
  );
  const message = computed(() => {
    if (!enabled || !initialized.value || isUnlimited.value) return '';
    if (canSend.value === false)
      return 'You have no free messages remaining. Please contact sales for more.';
    if (typeof remainingCredits.value === 'number') {
      return `You have ${remainingCredits.value} free messages remaining.`;
    }
    return '';
  });

  async function setFromEndpoint(
    endpoint: '/credits' | '/credits/consume',
    method: 'GET' | 'POST'
  ) {
    if (!enabled) return status.value;
    loading.value = true;
    error.value = '';

    try {
      const { response } = await callClientPortal(
        endpoint,
        { method },
        {
          base: EDITAGENT_API_BASE
        }
      );
      status.value = response.data as EditAgentCreditsStatus;
      initialized.value = true;
      return status.value;
    } catch (err: any) {
      error.value = err?.message || 'Failed to load edit agent credits';
      initialized.value = true;
      return status.value;
    } finally {
      loading.value = false;
    }
  }

  async function initialize() {
    return await refresh();
  }

  async function refresh() {
    return await setFromEndpoint('/credits', 'GET');
  }

  async function consume() {
    return await setFromEndpoint('/credits/consume', 'POST');
  }

  async function fill(remainingCredits: number) {
    if (!enabled) return status.value;
    loading.value = true;
    error.value = '';

    try {
      const { response } = await callClientPortal(
        '/credits/fill',
        {
          method: 'POST',
          body: JSON.stringify({ remainingCredits })
        },
        { base: EDITAGENT_API_BASE }
      );
      status.value = response.data as EditAgentCreditsStatus;
      initialized.value = true;
      return status.value;
    } catch (err: any) {
      error.value = err?.message || 'Failed to fill edit agent credits';
      initialized.value = true;
      return status.value;
    } finally {
      loading.value = false;
    }
  }

  window.fillEditAgentCredits = async (remainingCredits: number) => {
    const nextStatus = await fill(remainingCredits);
    return nextStatus ?? null;
  };

  window.consumeEditAgentCredits = async () => {
    const nextStatus = await consume();
    return nextStatus ?? null;
  };

  return {
    status,
    loading,
    initialized,
    error,
    isUnlimited,
    canSend,
    remainingCredits,
    message,
    initialize,
    refresh,
    consume,
    fill
  };
}
